import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Save, Eye, Globe, EyeOff, ArrowLeft, Plus, Trash2,
  GripVertical, ChevronUp, ChevronDown, Settings2, Type, Image,
  FileText, Users, MapPin, Video, Phone, Layout, Sparkles, LayoutGrid,
  CheckSquare, List, Mail, Hash, Calendar, ToggleLeft, X, Download, FolderOpen,
  Copy, FileStack
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { landingPagesApi, brandApi } from '@/api/landingPages';
import { apiV1 } from '@/api/client';
import { ImagePicker } from '@/components/ImagePicker';
import { VideoPicker } from '@/components/VideoPicker';
import { HeroVideoBackground, canPlayAsBackground } from '@/components/HeroVideoBackground';
import { playerEmbedUrl } from '@/lib/videoEmbed';
import { FilePicker } from '@/components/FilePicker';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import BrandFooter from '@/components/brand/BrandFooter';
import TrackingTagsEditor from '@/components/tracking/TrackingTagsEditor';
import LeadTrackingEditor from '@/components/tracking/LeadTrackingEditor';
import TranslationEditor from '@/components/i18n/TranslationEditor';
import { heroAlignment } from '@/lib/heroAlignment';

const MARKETING_CONSENT_DEFAULT = 'I agree to receive marketing communication from Pam Golding Properties.';

const SECTION_TYPES = [
  { type: 'hero', label: 'Hero Banner', icon: Layout, description: 'Full-width header with image/video' },
  { type: 'logo_overlay', label: 'Logo Overlay', icon: Image, description: 'Centered logo overlaying hero' },
  { type: 'text', label: 'Text Block', icon: Type, description: 'Title and body text' },
  { type: 'image_gallery', label: 'Image Gallery', icon: Image, description: 'Photo grid or carousel' },
  { type: 'property_details', label: 'Property Details', icon: LayoutGrid, description: 'Price, beds, baths info' },
  { type: 'features', label: 'Features List', icon: Sparkles, description: 'Icon and text features' },
  { type: 'agent_card', label: 'Agent Card', icon: Users, description: 'Agent profile info' },
  { type: 'dynamic_agent', label: 'Dynamic Agent', icon: Users, description: 'Agent from database with gallery' },
  { type: 'map', label: 'Location Map', icon: MapPin, description: 'Interactive map' },
  { type: 'video', label: 'Video Embed', icon: Video, description: 'YouTube/Vimeo video' },
  { type: 'contact_form', label: 'Contact Form', icon: Phone, description: 'Lead capture form' },
  { type: 'pdf_download', label: 'PDF Download', icon: Download, description: 'Single PDF download button' },
  { type: 'document_list', label: 'Document List', icon: FolderOpen, description: 'Grid of downloadable documents' },
];

const FIELD_TYPES = [
  { type: 'text', label: 'Text Input', icon: Type, description: 'Single line text' },
  { type: 'email', label: 'Email', icon: Mail, description: 'Email address' },
  { type: 'phone', label: 'Phone', icon: Phone, description: 'Phone number' },
  { type: 'textarea', label: 'Text Area', icon: FileText, description: 'Multi-line text' },
  { type: 'select', label: 'Dropdown', icon: List, description: 'Select from options' },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, description: 'Yes/No toggle' },
  { type: 'radio', label: 'Radio Buttons', icon: ToggleLeft, description: 'Single choice' },
  { type: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { type: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
];

// Section Editor Components
function HeroEditor({ content, onChange, pageId }) {
  const backgroundType = content.background_type || 'image';
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Headline</Label>
        <Input
          value={content.headline || ''}
          onChange={(e) => onChange({ ...content, headline: e.target.value })}
          placeholder="Your main headline"
        />
      </div>
      <div>
        <Label>Subheadline</Label>
        <Input
          value={content.subheadline || ''}
          onChange={(e) => onChange({ ...content, subheadline: e.target.value })}
          placeholder="Supporting text"
        />
      </div>
      <div>
        <Label>Text Alignment</Label>
        <Select
          value={content.alignment || 'center'}
          onValueChange={(v) => onChange({ ...content, alignment: v })}
        >
          <SelectTrigger data-testid="hero-alignment"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">Aligns the headline, subheadline and button.</p>
      </div>
      
      {/* Background Type Selector */}
      <div>
        <Label>Background Type</Label>
        <Select
          value={backgroundType}
          onValueChange={(v) => onChange({ 
            ...content, 
            background_type: v,
            // Clear the other type when switching
            ...(v === 'image' ? { background_video: null } : { background_image: null })
          })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="image">Image</SelectItem>
            <SelectItem value="video">Video (upload, YouTube or Vimeo)</SelectItem>
            <SelectItem value="gradient">Gradient</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Image Background */}
      {backgroundType === 'image' && (
        <ImagePicker
          label="Background Image"
          value={content.background_image || ''}
          onChange={(url, fallbackUrl) => onChange({ 
            ...content, 
            background_image: url,
            background_image_fallback: fallbackUrl 
          })}
          placeholder="Select or upload background image"
          aspectRatio="16/9"
          folder={pageId ? `pages/${pageId}` : 'pages'}
          testId="hero-background-picker"
        />
      )}
      
      {/* Video Background */}
      {backgroundType === 'video' && (
        <div className="space-y-4">
          <VideoPicker
            label="Background Video"
            value={content.background_video || ''}
            onChange={(url) => onChange({ ...content, background_video: url })}
            placeholder="Upload an MP4, or paste a YouTube or Vimeo link"
            folder={pageId ? `pages/${pageId}` : 'pages'}
            testId="hero-video-picker"
          />
          {content.background_video && !canPlayAsBackground(content.background_video) && (
            <p className="text-xs text-destructive" data-testid="hero-video-unsupported">
              This link can't play as a background. Upload an MP4 or WebM, or paste a YouTube or Vimeo link.
            </p>
          )}
          
          {/* Video Options */}
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
            <div className="space-y-0.5">
              <Label htmlFor="video-loop" className="text-sm font-medium">Auto Loop Video</Label>
              <p className="text-xs text-muted-foreground">Automatically restart video when it ends</p>
            </div>
            <Switch
              id="video-loop"
              checked={content.video_loop !== false}
              onCheckedChange={(checked) => onChange({ ...content, video_loop: checked })}
              data-testid="hero-video-loop-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
            <div className="space-y-0.5">
              <Label htmlFor="video-muted" className="text-sm font-medium">Mute Video</Label>
              <p className="text-xs text-muted-foreground">Play video without sound (YouTube and Vimeo backgrounds are always muted)</p>
            </div>
            <Switch
              id="video-muted"
              checked={content.video_muted !== false}
              onCheckedChange={(checked) => onChange({ ...content, video_muted: checked })}
              data-testid="hero-video-muted-toggle"
            />
          </div>
        </div>
      )}
      
      {/* Gradient Background */}
      {backgroundType === 'gradient' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Start Color</Label>
            <Input
              type="color"
              value={content.gradient_start || '#144733'}
              onChange={(e) => onChange({ ...content, gradient_start: e.target.value })}
              className="h-10 p-1"
            />
          </div>
          <div>
            <Label>End Color</Label>
            <Input
              type="color"
              value={content.gradient_end || '#1e6e5c'}
              onChange={(e) => onChange({ ...content, gradient_end: e.target.value })}
              className="h-10 p-1"
            />
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Height</Label>
          <Select
            value={content.height || '60vh'}
            onValueChange={(v) => onChange({ ...content, height: v })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="40vh">Small (40vh)</SelectItem>
              <SelectItem value="60vh">Medium (60vh)</SelectItem>
              <SelectItem value="80vh">Large (80vh)</SelectItem>
              <SelectItem value="100vh">Full Screen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Overlay Opacity</Label>
          <Input
            type="number"
            min="0"
            max="1"
            step="0.1"
            value={content.overlay_opacity ?? 0.4}
            onChange={(e) => onChange({ ...content, overlay_opacity: parseFloat(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
}

function TextEditor({ content, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Section title"
        />
      </div>
      <div>
        <Label>Body Text</Label>
        <Textarea
          value={content.body || ''}
          onChange={(e) => onChange({ ...content, body: e.target.value })}
          placeholder="Enter your content..."
          rows={5}
        />
      </div>
      <div>
        <Label>Text Alignment</Label>
        <Select
          value={content.alignment || 'left'}
          onValueChange={(v) => onChange({ ...content, alignment: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function LogoOverlayEditor({ content, onChange, pageId }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Note:</strong> This section displays a logo at the top of the page, overlaying the hero section. 
          If no custom logo is set, it will use your Brand Kit logo.
        </p>
      </div>
      
      <ImagePicker
        label="Custom Logo (optional)"
        value={content.logo_url || ''}
        onChange={(url, fallbackUrl) => onChange({ 
          ...content, 
          logo_url: url,
          logo_fallback_url: fallbackUrl 
        })}
        placeholder="Leave empty to use Brand Kit logo"
        aspectRatio="auto"
        folder={pageId ? `pages/${pageId}` : 'logos'}
        testId="logo-overlay-picker"
      />
      
      <div>
        <Label>Logo Size</Label>
        <Select
          value={content.size || 'large'}
          onValueChange={(v) => onChange({ ...content, size: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="small">Small</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="large">Large</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Background Style</Label>
        <Select
          value={content.background_style || 'none'}
          onValueChange={(v) => onChange({ ...content, background_style: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None (transparent)</SelectItem>
            <SelectItem value="solid">Solid Color</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {content.background_style !== 'none' && (
        <div>
          <Label>Background Color</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              value={content.background_color || '#ffffff'}
              onChange={(e) => onChange({ ...content, background_color: e.target.value })}
              className="h-10 w-14 p-1"
            />
            <Input
              value={content.background_color || '#ffffff'}
              onChange={(e) => onChange({ ...content, background_color: e.target.value })}
              placeholder="#ffffff"
              className="flex-1"
            />
          </div>
        </div>
      )}
      
      <div>
        <Label>Border</Label>
        <Select
          value={content.border_style || 'none'}
          onValueChange={(v) => onChange({ ...content, border_style: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Border</SelectItem>
            <SelectItem value="thin">Thin Border</SelectItem>
            <SelectItem value="medium">Medium Border</SelectItem>
            <SelectItem value="thick">Thick Border</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {content.border_style && content.border_style !== 'none' && (
        <div>
          <Label>Border Color</Label>
          <div className="flex gap-2">
            <Input
              type="color"
              value={content.border_color || '#e5e7eb'}
              onChange={(e) => onChange({ ...content, border_color: e.target.value })}
              className="h-10 w-14 p-1"
            />
            <Input
              value={content.border_color || '#e5e7eb'}
              onChange={(e) => onChange({ ...content, border_color: e.target.value })}
              placeholder="#e5e7eb"
              className="flex-1"
            />
          </div>
        </div>
      )}
      
      <div>
        <Label>Position from Top</Label>
        <Select
          value={content.top_position || '0px'}
          onValueChange={(v) => onChange({ ...content, top_position: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0px">Flush with top</SelectItem>
            <SelectItem value="10px">10px from top</SelectItem>
            <SelectItem value="20px">20px from top</SelectItem>
            <SelectItem value="40px">40px from top</SelectItem>
            <SelectItem value="60px">60px from top</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Logo Shape</Label>
        <Select
          value={content.shape || 'square'}
          onValueChange={(v) => onChange({ ...content, shape: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="square">Square</SelectItem>
            <SelectItem value="rounded">Rounded</SelectItem>
            <SelectItem value="circle">Circle</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function PropertyDetailsEditor({ content, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Price</Label>
          <Input
            value={content.price || ''}
            onChange={(e) => onChange({ ...content, price: e.target.value })}
            placeholder="R 4,500,000"
          />
        </div>
        <div>
          <Label>Price Label</Label>
          <Input
            value={content.price_label || ''}
            onChange={(e) => onChange({ ...content, price_label: e.target.value })}
            placeholder="Asking Price"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Bedrooms</Label>
          <Input
            value={content.bedrooms || ''}
            onChange={(e) => onChange({ ...content, bedrooms: e.target.value })}
          />
        </div>
        <div>
          <Label>Bathrooms</Label>
          <Input
            value={content.bathrooms || ''}
            onChange={(e) => onChange({ ...content, bathrooms: e.target.value })}
          />
        </div>
        <div>
          <Label>Garages</Label>
          <Input
            value={content.garages || ''}
            onChange={(e) => onChange({ ...content, garages: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Property Size</Label>
          <Input
            value={content.property_size || ''}
            onChange={(e) => onChange({ ...content, property_size: e.target.value })}
            placeholder="450 m²"
          />
        </div>
        <div>
          <Label>Land Size</Label>
          <Input
            value={content.land_size || ''}
            onChange={(e) => onChange({ ...content, land_size: e.target.value })}
            placeholder="1,200 m²"
          />
        </div>
      </div>
    </div>
  );
}

function AgentCardEditor({ content, onChange, pageId }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Agent Name</Label>
        <Input
          value={content.agent_name || ''}
          onChange={(e) => onChange({ ...content, agent_name: e.target.value })}
        />
      </div>
      <div>
        <Label>Title/Role</Label>
        <Input
          value={content.agent_title || ''}
          onChange={(e) => onChange({ ...content, agent_title: e.target.value })}
          placeholder="Property Consultant"
        />
      </div>
      <ImagePicker
        label="Agent Photo"
        value={content.agent_photo || ''}
        onChange={(url, fallbackUrl) => onChange({ 
          ...content, 
          agent_photo: url,
          agent_photo_fallback: fallbackUrl 
        })}
        placeholder="Select or upload agent photo"
        aspectRatio="1/1"
        folder={pageId ? `pages/${pageId}` : 'agents'}
        testId="agent-photo-picker"
      />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Phone</Label>
          <Input
            value={content.agent_phone || ''}
            onChange={(e) => onChange({ ...content, agent_phone: e.target.value })}
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input
            value={content.agent_email || ''}
            onChange={(e) => onChange({ ...content, agent_email: e.target.value })}
          />
        </div>
      </div>
      <div>
        <Label>Bio</Label>
        <Textarea
          value={content.agent_bio || ''}
          onChange={(e) => onChange({ ...content, agent_bio: e.target.value })}
          rows={3}
        />
      </div>
    </div>
  );
}

function DynamicAgentEditor({ content, onChange }) {
  const [referenceData, setReferenceData] = useState({ regions: [], offices: [], agents: [] });
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentDetails, setAgentDetails] = useState(null);
  const [agentSearch, setAgentSearch] = useState('');
  
  // Image type options for display
  const IMAGE_TYPES = [
    { value: 'high_res', label: 'High Resolution Portrait' },
    { value: 'landscape', label: 'Landscape' },
    { value: 'portrait', label: 'Portrait' },
    { value: 'business_card', label: 'Business Card' },
    { value: 'alchemy', label: 'Alchemy' },
    { value: 'sale_boards', label: 'Sale Boards' },
  ];
  
  // Fetch reference data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Via the shared API client, which carries the SSO session cookie and
        // the real stored token. This used to be a raw fetch sending
        // `Bearer ${localStorage.getItem('access_token')}` — a key nothing ever
        // sets, so every user sent "Bearer null", the server took the Bearer
        // path, rejected it, and never consulted the cookie: the dynamic-agent
        // picker failed to load for everyone.
        const { data } = await apiV1.get('/reference-data/options');
        if (data?.success) {
          setReferenceData({
            regions: data.data.region || [],
            offices: data.data.office || [],
            agents: data.data.agent || []
          });
          setFilteredOffices(data.data.office || []);
          setFilteredAgents(data.data.agent || []);
        }
      } catch (error) {
        console.error('Failed to load reference data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  // Filter offices when region changes
  useEffect(() => {
    if (content.region_id) {
      const regionOffices = referenceData.offices.filter(o => o.region_id === content.region_id);
      setFilteredOffices(regionOffices);
      if (content.office_id && !regionOffices.some(o => o.id === content.office_id)) {
        onChange({ ...content, office_id: '', agent_id: '' });
      }
    } else {
      setFilteredOffices(referenceData.offices);
    }
  }, [content.region_id, referenceData.offices]);
  
  // Filter agents when office/region changes
  useEffect(() => {
    let agents = referenceData.agents;
    if (content.office_id) {
      agents = agents.filter(a => a.office_id === content.office_id);
    } else if (content.region_id) {
      const regionOfficeIds = filteredOffices.map(o => o.id);
      agents = agents.filter(a => regionOfficeIds.includes(a.office_id));
    }
    setFilteredAgents(agents);
    if (content.agent_id && !agents.some(a => a.id === content.agent_id)) {
      onChange({ ...content, agent_id: '' });
    }
  }, [content.office_id, content.region_id, filteredOffices, referenceData.agents]);
  
  // Fetch full agent details when agent selected
  useEffect(() => {
    const fetchAgentDetails = async () => {
      if (!content.agent_id) {
        setAgentDetails(null);
        return;
      }
      try {
        // Look the agent up by id rather than listing 200 and searching the
        // page: with ~2 500 agents most selections were never in that first
        // page, and the raw fetch sent "Bearer null" (see the options fetch).
        const { data } = await apiV1.get(`/reference-data/${content.agent_id}`);
        if (data?.success) {
          setAgentDetails(data.data);
        }
      } catch (error) {
        console.error('Failed to load agent details:', error);
      }
    };
    fetchAgentDetails();
  }, [content.agent_id]);
  
  // Get search-filtered agents
  const searchFilteredAgents = agentSearch.trim() 
    ? filteredAgents.filter(a => a.name.toLowerCase().includes(agentSearch.toLowerCase()))
    : filteredAgents;
  
  // Get selected agent name
  const selectedAgentName = content.agent_id 
    ? (filteredAgents.find(a => a.id === content.agent_id)?.name || agentDetails?.name || 'Agent selected')
    : null;
  
  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          <strong>Dynamic Agent:</strong> Select an agent from your database. Their profile and selected image will be displayed.
        </p>
      </div>
      
      <div>
        <Label>Section Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Meet Your Agent"
          data-testid="dynamic-agent-title"
        />
      </div>
      
      <Separator />
      
      <div className="grid grid-cols-2 gap-3">
        {/* Region Selector */}
        <div>
          <Label className="text-xs">Region (optional)</Label>
          <Select 
            value={content.region_id || "all"} 
            onValueChange={(v) => onChange({ ...content, region_id: v === "all" ? '' : v, office_id: '', agent_id: '' })}
          >
            <SelectTrigger data-testid="dynamic-agent-region-select">
              <SelectValue placeholder="All regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All regions</SelectItem>
              {referenceData.regions.map(region => (
                <SelectItem key={region.id} value={region.id}>{region.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Office Selector */}
        <div>
          <Label className="text-xs">Office (optional)</Label>
          <Select 
            value={content.office_id || "all"} 
            onValueChange={(v) => onChange({ ...content, office_id: v === "all" ? '' : v, agent_id: '' })}
          >
            <SelectTrigger data-testid="dynamic-agent-office-select">
              <SelectValue placeholder="All offices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All offices</SelectItem>
              {filteredOffices.slice(0, 100).map(office => (
                <SelectItem key={office.id} value={office.id}>{office.name}</SelectItem>
              ))}
              {filteredOffices.length > 100 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
                  ... and {filteredOffices.length - 100} more (select region to filter)
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Agent Selector with Search */}
      <div>
        <Label className="text-xs">Agent *</Label>
        <div className="space-y-2">
          <Input
            placeholder="Search agents by name..."
            value={agentSearch}
            onChange={(e) => setAgentSearch(e.target.value)}
            className="mb-2"
            data-testid="dynamic-agent-search"
          />
          {selectedAgentName && (
            <div className="flex items-center justify-between p-2 bg-primary/10 rounded border border-primary/20">
              <span className="text-sm font-medium">{selectedAgentName}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onChange({ ...content, agent_id: '' })}
                className="h-6 px-2"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          <ScrollArea className="h-48 border rounded-md">
            <div className="p-2 space-y-1">
              {searchFilteredAgents.length === 0 ? (
                <p className="text-sm text-muted-foreground p-2 text-center">
                  {agentSearch ? 'No agents found' : 'No agents available'}
                </p>
              ) : (
                searchFilteredAgents.map(agent => (
                  <div 
                    key={agent.id}
                    className={`p-2 rounded cursor-pointer transition-colors ${
                      content.agent_id === agent.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => onChange({ ...content, agent_id: agent.id })}
                  >
                    <span className="text-sm">{agent.name}</span>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
          <p className="text-xs text-muted-foreground">
            {filteredAgents.length} agents available {agentSearch && `(${searchFilteredAgents.length} matching)`}
          </p>
        </div>
      </div>
      
      {/* Agent Preview */}
      {agentDetails && (
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              {agentDetails.images?.high_res ? (
                <img 
                  src={agentDetails.images.high_res} 
                  alt={agentDetails.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <span className="text-xl font-semibold text-muted-foreground">
                    {agentDetails.first_name?.[0]}{agentDetails.last_name?.[0]}
                  </span>
                </div>
              )}
              <div className="flex-1">
                <p className="font-semibold">{agentDetails.name}</p>
                <p className="text-sm text-muted-foreground">{agentDetails.email}</p>
                <p className="text-sm text-muted-foreground">{agentDetails.phone}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {agentDetails.is_emerald_circle && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Emerald Circle</Badge>
                  )}
                  {agentDetails.is_gold_club && (
                    <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">Gold Club</Badge>
                  )}
                  {agentDetails.has_ffc && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">FFC: {agentDetails.ffc_type}</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {!content.agent_id && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          Please select an agent to display on this section.
        </p>
      )}
      
      <Separator />
      
      {/* Image Selection */}
      {agentDetails && (
        <div>
          <Label>Display Image</Label>
          <p className="text-xs text-muted-foreground mb-2">Choose which agent image to show on the page</p>
          <Select 
            value={content.display_image || "high_res"} 
            onValueChange={(v) => onChange({ ...content, display_image: v })}
          >
            <SelectTrigger data-testid="dynamic-agent-image-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMAGE_TYPES.filter(type => agentDetails.images?.[type.value]).map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {content.display_image && agentDetails.images?.[content.display_image] && (
            <div className="mt-2">
              <img 
                src={agentDetails.images[content.display_image]} 
                alt="Selected display image"
                className="w-full max-w-xs h-32 object-cover rounded border"
              />
            </div>
          )}
        </div>
      )}
      
      <div>
        <Label>Display Layout</Label>
        <Select 
          value={content.layout || "card"} 
          onValueChange={(v) => onChange({ ...content, layout: v })}
        >
          <SelectTrigger data-testid="dynamic-agent-layout-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="card">Card (compact)</SelectItem>
            <SelectItem value="full">Full Profile with Gallery</SelectItem>
            <SelectItem value="minimal">Minimal (name & contact)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="show_badges"
          checked={content.show_badges !== false}
          onCheckedChange={(checked) => onChange({ ...content, show_badges: checked })}
        />
        <Label htmlFor="show_badges" className="text-sm">Show recognition badges</Label>
      </div>
      
      <div className="flex items-center space-x-2">
        <Checkbox
          id="show_gallery"
          checked={content.show_gallery !== false}
          onCheckedChange={(checked) => onChange({ ...content, show_gallery: checked })}
        />
        <Label htmlFor="show_gallery" className="text-sm">Show image gallery (Full layout only)</Label>
      </div>
    </div>
  );
}

function ContactFormEditor({ content, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Contact Us"
        />
      </div>
      <div>
        <Label>Subtitle</Label>
        <Input
          value={content.subtitle || ''}
          onChange={(e) => onChange({ ...content, subtitle: e.target.value })}
          placeholder="We'd love to hear from you"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Form fields are configured in the Form Builder tab.
      </p>
    </div>
  );
}

function ImageGalleryEditor({ content, onChange, pageId }) {
  const [images, setImages] = useState(content.images || []);
  
  const addImage = (url, fallbackUrl) => {
    const newImage = {
      id: `img_${Date.now()}`,
      url,
      fallback_url: fallbackUrl || null,
      alt: ''
    };
    const updatedImages = [...images, newImage];
    setImages(updatedImages);
    onChange({ ...content, images: updatedImages });
  };
  
  const removeImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    setImages(updatedImages);
    onChange({ ...content, images: updatedImages });
  };
  
  const updateImageAlt = (imageId, alt) => {
    const updatedImages = images.map(img => 
      img.id === imageId ? { ...img, alt } : img
    );
    setImages(updatedImages);
    onChange({ ...content, images: updatedImages });
  };
  
  // Ensure folder exists for this page
  const pageFolder = pageId ? `pages/${pageId}` : 'pages';
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Photo Gallery"
        />
      </div>
      
      <div>
        <Label>Layout</Label>
        <Select
          value={content.layout || 'grid'}
          onValueChange={(v) => onChange({ ...content, layout: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="grid">Grid (3 columns)</SelectItem>
            <SelectItem value="grid-2">Grid (2 columns)</SelectItem>
            <SelectItem value="masonry">Masonry</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Images ({images.length})</Label>
          <span className="text-xs text-muted-foreground">Saved to: {pageFolder}</span>
        </div>
        
        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {images.map((img) => (
              <div key={img.id} className="relative group aspect-video bg-muted rounded overflow-hidden">
                <img 
                  src={img.url} 
                  alt={img.alt || ''} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => removeImage(img.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <ImagePicker
          label="Add Image"
          value=""
          onChange={(url, fallbackUrl) => addImage(url, fallbackUrl)}
          placeholder="Select or upload an image"
          aspectRatio="16/9"
          folder={pageFolder}
          testId="gallery-image-picker"
        />
      </div>
    </div>
  );
}

function FeaturesEditor({ content, onChange }) {
  const [features, setFeatures] = useState(content.features || []);
  const [newFeature, setNewFeature] = useState('');
  
  const addFeature = () => {
    if (!newFeature.trim()) return;
    const feature = {
      id: `feat_${Date.now()}`,
      title: newFeature.trim(),
      description: ''
    };
    const updatedFeatures = [...features, feature];
    setFeatures(updatedFeatures);
    onChange({ ...content, features: updatedFeatures });
    setNewFeature('');
  };
  
  const removeFeature = (featureId) => {
    const updatedFeatures = features.filter(f => f.id !== featureId);
    setFeatures(updatedFeatures);
    onChange({ ...content, features: updatedFeatures });
  };
  
  const updateFeature = (featureId, updates) => {
    const updatedFeatures = features.map(f => 
      f.id === featureId ? { ...f, ...updates } : f
    );
    setFeatures(updatedFeatures);
    onChange({ ...content, features: updatedFeatures });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Features & Highlights"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Features ({features.length})</Label>
        
        {features.length > 0 && (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {features.map((feature) => (
              <div key={feature.id} className="flex items-start gap-2 p-2 bg-muted/50 rounded">
                <div className="flex-1 space-y-1">
                  <Input
                    value={feature.title || ''}
                    onChange={(e) => updateFeature(feature.id, { title: e.target.value })}
                    placeholder="Feature title"
                    className="h-8 text-sm"
                  />
                  <Input
                    value={feature.description || ''}
                    onChange={(e) => updateFeature(feature.id, { description: e.target.value })}
                    placeholder="Description (optional)"
                    className="h-7 text-xs"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => removeFeature(feature.id)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex gap-2">
          <Input
            value={newFeature}
            onChange={(e) => setNewFeature(e.target.value)}
            placeholder="Add a new feature..."
            onKeyDown={(e) => e.key === 'Enter' && addFeature()}
          />
          <Button onClick={addFeature} size="sm">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function MapEditor({ content, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Location"
        />
      </div>
      
      <div>
        <Label>Address</Label>
        <Input
          value={content.address || ''}
          onChange={(e) => onChange({ ...content, address: e.target.value })}
          placeholder="123 Main Street, City, Country"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Displayed above the map
        </p>
      </div>
      
      <div>
        <Label>Google Maps Embed URL</Label>
        <Textarea
          value={content.embed_url || ''}
          onChange={(e) => onChange({ ...content, embed_url: e.target.value })}
          placeholder="https://www.google.com/maps/embed?pb=..."
          rows={3}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Go to Google Maps → Share → Embed a map → Copy the src URL from the iframe code
        </p>
      </div>
      
      <div>
        <Label>Map Height</Label>
        <Select
          value={content.height || '400px'}
          onValueChange={(v) => onChange({ ...content, height: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="300px">Small (300px)</SelectItem>
            <SelectItem value="400px">Medium (400px)</SelectItem>
            <SelectItem value="500px">Large (500px)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function VideoEditor({ content, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Watch the Video"
        />
      </div>
      
      <div>
        <Label>Video URL</Label>
        <Input
          value={content.video_url || ''}
          onChange={(e) => onChange({ ...content, video_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..."
        />
        <p className="text-xs text-muted-foreground mt-1">
          Supports YouTube and Vimeo URLs
        </p>
      </div>
      
      <div>
        <Label>Video Description</Label>
        <Textarea
          value={content.description || ''}
          onChange={(e) => onChange({ ...content, description: e.target.value })}
          placeholder="Optional description below the video"
          rows={2}
        />
      </div>
      
      <div className="flex items-center gap-2">
        <Checkbox
          checked={content.autoplay || false}
          onCheckedChange={(v) => onChange({ ...content, autoplay: v })}
        />
        <Label className="font-normal">Autoplay (muted)</Label>
      </div>
    </div>
  );
}

function PDFDownloadEditor({ content, onChange, pageId }) {
  return (
    <div className="space-y-4">
      <div className="p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>PDF Download:</strong> Displays a button for visitors to download a PDF document.
        </p>
      </div>
      
      <div>
        <Label>Section Title (optional)</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Download Our Brochure"
        />
      </div>
      
      <div>
        <Label>Description (optional)</Label>
        <Textarea
          value={content.description || ''}
          onChange={(e) => onChange({ ...content, description: e.target.value })}
          placeholder="Get our comprehensive property brochure with all the details..."
          rows={2}
        />
      </div>
      
      <FilePicker
        label="PDF File"
        value={content.pdf_url || ''}
        onChange={(url, filename) => onChange({ 
          ...content, 
          pdf_url: url,
          pdf_filename: filename 
        })}
        folder={pageId ? `pages/${pageId}/documents` : 'documents'}
        testId="pdf-download-picker"
      />
      
      <div>
        <Label>Button Text</Label>
        <Input
          value={content.button_text || ''}
          onChange={(e) => onChange({ ...content, button_text: e.target.value })}
          placeholder="Download PDF"
        />
      </div>
      
      <div>
        <Label>Button Style</Label>
        <Select
          value={content.button_style || 'primary'}
          onValueChange={(v) => onChange({ ...content, button_style: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="primary">Primary (Filled)</SelectItem>
            <SelectItem value="outline">Outline</SelectItem>
            <SelectItem value="ghost">Ghost (Text only)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>Alignment</Label>
        <Select
          value={content.alignment || 'center'}
          onValueChange={(v) => onChange({ ...content, alignment: v })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function DocumentListEditor({ content, onChange, pageId }) {
  const documents = content.documents || [];
  
  const addDocument = () => {
    onChange({
      ...content,
      documents: [
        ...documents,
        {
          id: `doc_${Date.now()}`,
          title: '',
          description: '',
          image_url: '',
          pdf_url: '',
          pdf_filename: ''
        }
      ]
    });
  };
  
  const updateDocument = (index, updates) => {
    const newDocs = [...documents];
    newDocs[index] = { ...newDocs[index], ...updates };
    onChange({ ...content, documents: newDocs });
  };
  
  const removeDocument = (index) => {
    const newDocs = documents.filter((_, i) => i !== index);
    onChange({ ...content, documents: newDocs });
  };
  
  return (
    <div className="space-y-4">
      <div className="p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Document List:</strong> Displays a grid of downloadable documents with images and descriptions. Shows 3 items per row.
        </p>
      </div>
      
      <div>
        <Label>Section Title (optional)</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
          placeholder="Downloads & Resources"
        />
      </div>
      
      <div>
        <Label>Section Description (optional)</Label>
        <Textarea
          value={content.description || ''}
          onChange={(e) => onChange({ ...content, description: e.target.value })}
          placeholder="Access our collection of property documents and resources..."
          rows={2}
        />
      </div>
      
      <Separator />
      
      <div className="flex items-center justify-between">
        <Label>Documents ({documents.length})</Label>
        <Button type="button" variant="outline" size="sm" onClick={addDocument}>
          <Plus className="w-4 h-4 mr-1" />
          Add Document
        </Button>
      </div>
      
      <div className="space-y-4">
        {documents.map((doc, index) => (
          <Card key={doc.id || index} className="relative">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-8 w-8 p-0 text-destructive hover:text-destructive"
              onClick={() => removeDocument(index)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            
            <CardContent className="pt-4 space-y-3">
              <div className="text-sm font-medium text-muted-foreground mb-2">
                Document {index + 1}
              </div>
              
              <ImagePicker
                label="Cover Image"
                value={doc.image_url || ''}
                onChange={(url, fallbackUrl) => updateDocument(index, { 
                  image_url: url,
                  image_fallback_url: fallbackUrl 
                })}
                aspectRatio="16/9"
                folder={pageId ? `pages/${pageId}/documents` : 'documents'}
                testId={`doc-image-${index}`}
              />
              
              <div>
                <Label>Document Title</Label>
                <Input
                  value={doc.title || ''}
                  onChange={(e) => updateDocument(index, { title: e.target.value })}
                  placeholder="Property Brochure"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={doc.description || ''}
                  onChange={(e) => updateDocument(index, { description: e.target.value })}
                  placeholder="Complete details about the property..."
                  rows={2}
                />
              </div>
              
              <FilePicker
                label="PDF File"
                value={doc.pdf_url || ''}
                onChange={(url, filename) => updateDocument(index, { 
                  pdf_url: url,
                  pdf_filename: filename 
                })}
                folder={pageId ? `pages/${pageId}/documents` : 'documents'}
                testId={`doc-pdf-${index}`}
              />
            </CardContent>
          </Card>
        ))}
        
        {documents.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No documents added yet</p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={addDocument}>
              <Plus className="w-4 h-4 mr-1" />
              Add Your First Document
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function GenericEditor({ content, onChange }) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Title</Label>
        <Input
          value={content.title || ''}
          onChange={(e) => onChange({ ...content, title: e.target.value })}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Additional configuration options for this section type.
      </p>
    </div>
  );
}

function SectionEditor({ section, onChange, pageId }) {
  const editors = {
    hero: HeroEditor,
    logo_overlay: LogoOverlayEditor,
    text: TextEditor,
    property_details: PropertyDetailsEditor,
    agent_card: AgentCardEditor,
    dynamic_agent: DynamicAgentEditor,
    contact_form: ContactFormEditor,
    image_gallery: ImageGalleryEditor,
    features: FeaturesEditor,
    map: MapEditor,
    video: VideoEditor,
    pdf_download: PDFDownloadEditor,
    document_list: DocumentListEditor,
  };
  const Editor = editors[section.type] || GenericEditor;
  return <Editor 
    content={section.content} 
    onChange={(c) => onChange({ ...section, content: c })} 
    pageId={pageId} 
  />;
}

// Form Builder Component
function FormBuilder({ form, onChange }) {
  const [selectedField, setSelectedField] = useState(null);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [editingOptions, setEditingOptions] = useState('');

  // Initialize form if not present
  const currentForm = form || {
    id: `form_${Date.now()}`,
    name: 'Contact Form',
    submit_button_text: 'Submit',
    success_message: 'Thank you! We\'ll be in touch soon.',
    fields: [],
    notification_emails: [],
    send_confirmation_email: false,
    include_source_tracking: true,
    create_lead: true
  };

  const updateForm = (updates) => {
    onChange({ ...currentForm, ...updates });
  };

  const addField = (type) => {
    const fieldConfig = FIELD_TYPES.find(f => f.type === type);
    const newField = {
      id: `field_${Date.now()}`,
      type,
      name: type === 'email' ? 'email' : type === 'phone' ? 'phone' : `field_${Date.now()}`,
      label: fieldConfig?.label || 'New Field',
      placeholder: '',
      required: false,
      order: (currentForm.fields?.length || 0) + 1,
      options: type === 'select' || type === 'radio' ? [
        { label: 'Option 1', value: 'option_1' },
        { label: 'Option 2', value: 'option_2' }
      ] : []
    };
    updateForm({ fields: [...(currentForm.fields || []), newField] });
    setSelectedField(newField.id);
    setAddFieldOpen(false);
  };

  const updateField = (fieldId, updates) => {
    updateForm({
      fields: currentForm.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    });
  };

  const deleteField = (fieldId) => {
    updateForm({
      fields: currentForm.fields.filter(f => f.id !== fieldId)
    });
    setSelectedField(null);
  };

  const moveField = (fieldId, direction) => {
    const fields = [...currentForm.fields].sort((a, b) => a.order - b.order);
    const idx = fields.findIndex(f => f.id === fieldId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === fields.length - 1)) return;
    
    const newIdx = idx + direction;
    const [removed] = fields.splice(idx, 1);
    fields.splice(newIdx, 0, removed);
    
    const reordered = fields.map((f, i) => ({ ...f, order: i + 1 }));
    updateForm({ fields: reordered });
  };

  const currentField = currentForm.fields?.find(f => f.id === selectedField);

  const getFieldIcon = (type) => {
    const config = FIELD_TYPES.find(f => f.type === type);
    return config?.icon || Type;
  };

  return (
    <div className="space-y-4">
      {/* Form Settings */}
      <div className="space-y-3">
        <div>
          <Label>Form Name</Label>
          <Input
            value={currentForm.name || ''}
            onChange={(e) => updateForm({ name: e.target.value })}
            placeholder="Contact Form"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Submit Button Text</Label>
            <Input
              value={currentForm.submit_button_text || ''}
              onChange={(e) => updateForm({ submit_button_text: e.target.value })}
              placeholder="Submit"
            />
          </div>
        </div>
        <div>
          <Label>Success Message</Label>
          <Textarea
            value={currentForm.success_message || ''}
            onChange={(e) => updateForm({ success_message: e.target.value })}
            placeholder="Thank you for your enquiry!"
            rows={2}
          />
        </div>
      </div>

      <Separator />

      {/* Fields List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label>Form Fields</Label>
          <span className="text-xs text-muted-foreground">{currentForm.fields?.length || 0} fields</span>
        </div>
        
        <div className="space-y-2 mb-3">
          {currentForm.fields?.sort((a, b) => a.order - b.order).map((field) => {
            const FieldIcon = getFieldIcon(field.type);
            return (
              <div
                key={field.id}
                onClick={() => setSelectedField(field.id)}
                className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                  selectedField === field.id
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/50'
                }`}
              >
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                <FieldIcon className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">{field.label}</span>
                  <span className="text-xs text-muted-foreground">{field.type}</span>
                </div>
                {field.required && (
                  <Badge variant="secondary" className="text-xs">Required</Badge>
                )}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); moveField(field.id, -1); }}
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => { e.stopPropagation(); moveField(field.id, 1); }}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); deleteField(field.id); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Field Button */}
        <Dialog open={addFieldOpen} onOpenChange={setAddFieldOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="w-full" data-testid="add-form-field-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Field
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Form Field</DialogTitle>
              <DialogDescription>Choose a field type for your form</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 py-4">
              {FIELD_TYPES.map((ft) => (
                <div
                  key={ft.type}
                  onClick={() => addField(ft.type)}
                  className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors text-center"
                  data-testid={`add-field-${ft.type}`}
                >
                  <ft.icon className="w-5 h-5 mx-auto mb-2 text-primary" />
                  <p className="font-medium text-sm">{ft.label}</p>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Field Editor */}
      {currentField && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Edit Field</h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setSelectedField(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div>
              <Label>Label</Label>
              <Input
                value={currentField.label || ''}
                onChange={(e) => updateField(currentField.id, { label: e.target.value })}
                placeholder="Field label"
              />
            </div>

            <div>
              <Label>Field Name (for data)</Label>
              <Input
                value={currentField.name || ''}
                onChange={(e) => updateField(currentField.id, { name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="field_name"
                className="font-mono text-sm"
              />
            </div>

            <div>
              <Label>Placeholder</Label>
              <Input
                value={currentField.placeholder || ''}
                onChange={(e) => updateField(currentField.id, { placeholder: e.target.value })}
                placeholder="Enter placeholder text..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={currentField.required}
                onCheckedChange={(v) => updateField(currentField.id, { required: v })}
              />
              <Label className="font-normal">Required field</Label>
            </div>

            {/* Options for select/radio */}
            {(currentField.type === 'select' || currentField.type === 'radio') && (
              <div>
                <Label>Options (one per line)</Label>
                <Textarea
                  value={currentField.options?.map(o => o.label).join('\n') || ''}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n').filter(l => l.trim());
                    const options = lines.map((label, idx) => ({
                      label: label.trim(),
                      value: label.trim().toLowerCase().replace(/\s+/g, '_')
                    }));
                    updateField(currentField.id, { options });
                  }}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter each option on a new line
                </p>
              </div>
            )}

            {/* Min/Max for text fields */}
            {(currentField.type === 'text' || currentField.type === 'textarea') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min Length</Label>
                  <Input
                    type="number"
                    value={currentField.min_length || ''}
                    onChange={(e) => updateField(currentField.id, { min_length: parseInt(e.target.value) || null })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Max Length</Label>
                  <Input
                    type="number"
                    value={currentField.max_length || ''}
                    onChange={(e) => updateField(currentField.id, { max_length: parseInt(e.target.value) || null })}
                    placeholder="500"
                  />
                </div>
              </div>
            )}

            {/* Default value */}
            <div>
              <Label>Default Value (optional)</Label>
              <Input
                value={currentField.default_value || ''}
                onChange={(e) => updateField(currentField.id, { default_value: e.target.value })}
                placeholder="Pre-filled value"
              />
            </div>
          </div>
        </>
      )}

      <Separator />

      {/* Notification Settings */}
      <div className="space-y-3">
        <Label>Notification Emails</Label>
        <Input
          value={currentForm.notification_emails?.join(', ') || ''}
          onChange={(e) => updateForm({ 
            notification_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
          })}
          placeholder="email@example.com, another@example.com"
        />
        <p className="text-xs text-muted-foreground">
          Comma-separated list of emails to notify on new submissions
        </p>
        
        <div className="flex items-center gap-2">
          <Checkbox
            checked={currentForm.include_source_tracking}
            onCheckedChange={(v) => updateForm({ include_source_tracking: v })}
          />
          <Label className="font-normal">Include source tracking (UTM campaign tags on the page link)</Label>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            checked={currentForm.create_lead !== false}
            onCheckedChange={(v) => updateForm({ create_lead: !!v })}
            data-testid="create-lead-toggle"
          />
          <Label className="font-normal">Create a lead in Leads for each submission</Label>
        </div>
      </div>

      <Separator />

      {/* Visitor confirmation email */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!currentForm.send_confirmation_email}
            onCheckedChange={(v) => updateForm({ send_confirmation_email: !!v })}
            data-testid="confirmation-email-toggle"
          />
          <Label className="font-normal">Email the visitor a confirmation</Label>
        </div>
        {currentForm.send_confirmation_email && (
          <>
            <Input
              value={currentForm.confirmation_email_subject ?? 'Thank you for your enquiry'}
              onChange={(e) => updateForm({ confirmation_email_subject: e.target.value })}
              placeholder="Thank you for your enquiry"
            />
            <Textarea
              value={currentForm.confirmation_email_body || ''}
              onChange={(e) => updateForm({ confirmation_email_body: e.target.value })}
              rows={4}
              placeholder="Thank you for contacting Pam Golding Properties. We have received your details and one of our team will be in touch soon."
            />
          </>
        )}
        <p className="text-xs text-muted-foreground">
          Sent to the address in the form's Email field, in the language the visitor saw, at most once a
          day per address. Leave the message blank to use the standard wording.
        </p>
      </div>

      <Separator />

      {/* Marketing consent */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!currentForm.marketing_consent_enabled}
            onCheckedChange={(v) => updateForm({
              marketing_consent_enabled: !!v,
              marketing_consent_text: currentForm.marketing_consent_text || MARKETING_CONSENT_DEFAULT,
            })}
            data-testid="marketing-consent-toggle"
          />
          <Label className="font-normal">Ask for marketing consent</Label>
        </div>
        {currentForm.marketing_consent_enabled && (
          <Textarea
            value={currentForm.marketing_consent_text ?? MARKETING_CONSENT_DEFAULT}
            onChange={(e) => updateForm({ marketing_consent_text: e.target.value })}
            maxLength={500}
            rows={2}
          />
        )}
        <p className="text-xs text-muted-foreground">
          Shows an unticked opt-in checkbox above the submit button. Each submission records the answer
          and this wording, and the CSV export has a Marketing consent column. Only send marketing to
          people who opted in.
        </p>
      </div>
    </div>
  );
}

// Live Preview Component
function LivePreview({ page, brand }) {
  const { getPagesDomain, buildLandingPageUrl } = useAppSettings();
  
  const renderSection = (section) => {
    const { type, content } = section;
    
    switch (type) {
      case 'hero':
        const bgType = content.background_type || 'image';
        const getBackground = () => {
          if (bgType === 'gradient') {
            return `linear-gradient(135deg, ${content.gradient_start || '#144733'} 0%, ${content.gradient_end || '#1e6e5c'} 100%)`;
          }
          if (bgType === 'image' && content.background_image) {
            return `url(${content.background_image})`;
          }
          return 'linear-gradient(135deg, #144733 0%, #1e6e5c 100%)';
        };
        
        return (
          <div
            className={`relative flex items-center ${heroAlignment(content.alignment).row} text-white overflow-hidden`}
            style={{
              height: content.height || '60vh',
              minHeight: '200px',
            }}
          >
            {/* Video Background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: getBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            {bgType === 'video' && canPlayAsBackground(content.background_video) && (
              <HeroVideoBackground
                url={content.background_video}
                muted={content.video_muted !== false}
                loop={content.video_loop !== false}
              />
            )}
            <div
              className="absolute inset-0 bg-black"
              style={{ opacity: content.overlay_opacity ?? 0.4 }}
            />
            <div className={`relative z-10 ${heroAlignment(content.alignment).text} px-4 md:px-8`}>
              <h1 className="text-2xl md:text-4xl font-bold mb-2">{content.headline || 'Your Headline'}</h1>
              <p className="text-lg opacity-90">{content.subheadline || 'Supporting text'}</p>
            </div>
          </div>
        );
      
      case 'logo_overlay':
        const logoUrl = content.logo_url || brand?.logo_url;
        const sizeClasses = {
          small: 'h-12',
          medium: 'h-16',
          large: 'h-20',
        };
        const shapeClasses = {
          square: 'rounded-none',
          rounded: 'rounded-lg',
          circle: 'rounded-full',
        };
        const borderStyles = {
          none: 'none',
          thin: '1px solid',
          medium: '2px solid',
          thick: '4px solid',
        };
        
        // Get actual values with defaults matching the editor
        const backgroundStyle = content.background_style || 'none'; // Default to none (transparent)
        const borderStyle = content.border_style || 'none'; // Default to none
        
        // Check if container is needed (has background or border)
        const hasBackground = backgroundStyle !== 'none';
        const hasBorder = borderStyle !== 'none';
        const needsContainer = hasBackground || hasBorder;
        
        const logoContainerStyle = {
          backgroundColor: hasBackground ? (content.background_color || 'white') : 'transparent',
          border: hasBorder 
            ? `${borderStyles[borderStyle]} ${content.border_color || '#e5e7eb'}` 
            : 'none',
          position: 'absolute',
          top: content.top_position || '0px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 30,
        };
        
        // Only add padding and shadow when we have a container
        const paddingClass = needsContainer ? 'p-3' : '';
        const shadowClass = hasBackground ? 'shadow-lg' : '';
        
        return (
          <div 
            className={`${paddingClass} ${shadowClass} ${shapeClasses[content.shape || 'square']}`}
            style={logoContainerStyle}
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className={`${sizeClasses[content.size || 'large']} w-auto object-contain`} />
            ) : (
              <div className={`${sizeClasses[content.size || 'large']} w-24 bg-muted flex items-center justify-center text-xs text-muted-foreground rounded`}>
                Logo
              </div>
            )}
          </div>
        );
      
      case 'text':
        return (
          <div className="py-8 px-4" style={{ textAlign: content.alignment || 'left' }}>
            {content.title && <h2 className="text-xl font-semibold mb-3">{content.title}</h2>}
            <p className="text-muted-foreground whitespace-pre-wrap">{content.body || 'Your content here...'}</p>
          </div>
        );
      
      case 'property_details':
        return (
          <div className="py-6 px-4 bg-muted/30">
            <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
              {content.price && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">{content.price_label || 'Price'}</p>
                  <p className="text-2xl font-bold text-primary">{content.price}</p>
                </div>
              )}
            </div>
            <div className="flex justify-center gap-8 text-sm">
              {content.bedrooms && <div><span className="font-semibold">{content.bedrooms}</span> Beds</div>}
              {content.bathrooms && <div><span className="font-semibold">{content.bathrooms}</span> Baths</div>}
              {content.garages && <div><span className="font-semibold">{content.garages}</span> Garage</div>}
              {content.property_size && <div><span className="font-semibold">{content.property_size}</span></div>}
            </div>
          </div>
        );
      
      case 'agent_card':
        return (
          <div className="py-8 px-4">
            <div className="flex flex-col items-center text-center max-w-sm mx-auto">
              {content.agent_photo && (
                <div className="w-20 h-20 rounded-full overflow-hidden mb-3 bg-muted">
                  <img src={content.agent_photo} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <h3 className="font-semibold">{content.agent_name || 'Agent Name'}</h3>
              <p className="text-sm text-muted-foreground">{content.agent_title || 'Property Consultant'}</p>
              {content.agent_phone && <p className="text-sm mt-2">{content.agent_phone}</p>}
              {content.agent_email && <p className="text-sm text-primary">{content.agent_email}</p>}
            </div>
          </div>
        );
      
      case 'contact_form':
        return (
          <div className="py-8 px-4 bg-muted/20">
            <div className="max-w-md mx-auto text-center">
              {content.title && <h2 className="text-xl font-semibold mb-2">{content.title}</h2>}
              {content.subtitle && <p className="text-sm text-muted-foreground mb-4">{content.subtitle}</p>}
              <div className="space-y-3">
                {page?.form?.fields?.sort((a, b) => a.order - b.order).map((field) => (
                  <div key={field.id} className="text-left">
                    {field.type === 'textarea' ? (
                      <Textarea
                        placeholder={field.placeholder || field.label}
                        disabled
                        className="bg-background"
                      />
                    ) : field.type === 'select' ? (
                      <select
                        disabled
                        className="w-full h-10 px-3 rounded-md border bg-background text-muted-foreground"
                      >
                        <option>{field.placeholder || `Select ${field.label}`}</option>
                        {field.options?.map((opt, i) => (
                          <option key={i}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'checkbox' ? (
                      <div className="flex items-center gap-2">
                        <input type="checkbox" disabled className="rounded" />
                        <span className="text-sm">{field.label}</span>
                      </div>
                    ) : field.type === 'radio' ? (
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{field.label}</p>
                        {field.options?.map((opt, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input type="radio" disabled name={field.name} />
                            <span className="text-sm">{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Input
                        type={field.type}
                        placeholder={field.placeholder || field.label}
                        disabled
                        className="bg-background"
                      />
                    )}
                  </div>
                ))}
                {(!page?.form?.fields || page.form.fields.length === 0) && (
                  <>
                    <Input placeholder="Full Name" disabled className="bg-background" />
                    <Input placeholder="Email" disabled className="bg-background" />
                    <Input placeholder="Phone" disabled className="bg-background" />
                    <Textarea placeholder="Message" disabled className="bg-background" />
                  </>
                )}
                {page?.form?.marketing_consent_enabled && (
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" disabled className="mt-0.5" />
                    <span>{page.form.marketing_consent_text}</span>
                  </label>
                )}
                <Button className="w-full" disabled>
                  {page?.form?.submit_button_text || 'Submit'}
                </Button>
              </div>
            </div>
          </div>
        );
      
      case 'image_gallery':
        return (
          <div className="py-8 px-4">
            {content.title && <h2 className="text-xl font-semibold mb-4 text-center">{content.title}</h2>}
            {content.images && content.images.length > 0 ? (
              <div className={`grid gap-2 ${content.layout === 'grid-2' ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {content.images.slice(0, 6).map((img, idx) => (
                  <div key={img.id || idx} className="aspect-video bg-muted rounded overflow-hidden">
                    <img src={img.url || img} alt={img.alt || ''} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Image className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No images added yet</p>
              </div>
            )}
          </div>
        );
      
      case 'features':
        return (
          <div className="py-8 px-4">
            {content.title && <h2 className="text-xl font-semibold mb-4 text-center">{content.title}</h2>}
            {content.features && content.features.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
                {content.features.map((feat, idx) => (
                  <div key={feat.id || idx} className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{feat.title}</p>
                      {feat.description && <p className="text-xs text-muted-foreground">{feat.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Sparkles className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No features added yet</p>
              </div>
            )}
          </div>
        );
      
      case 'map':
        return (
          <div className="py-8 px-4">
            {content.title && <h2 className="text-xl font-semibold mb-2 text-center">{content.title}</h2>}
            {content.address && <p className="text-sm text-muted-foreground text-center mb-4">{content.address}</p>}
            {content.embed_url ? (
              <div className="rounded-lg overflow-hidden" style={{ height: content.height || '300px' }}>
                <iframe
                  src={content.embed_url}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            ) : (
              <div className="bg-muted rounded-lg flex items-center justify-center" style={{ height: content.height || '300px' }}>
                <div className="text-center text-muted-foreground">
                  <MapPin className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Add a Google Maps embed URL</p>
                </div>
              </div>
            )}
          </div>
        );
      
      case 'video':
        const embedUrl = playerEmbedUrl(content.video_url);
        
        return (
          <div className="py-8 px-4">
            {content.title && <h2 className="text-xl font-semibold mb-4 text-center">{content.title}</h2>}
            {embedUrl ? (
              <div className="aspect-video rounded-lg overflow-hidden bg-black max-w-2xl mx-auto">
                <iframe
                  src={embedUrl}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center max-w-2xl mx-auto">
                <div className="text-center text-muted-foreground">
                  <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Add a YouTube or Vimeo URL</p>
                </div>
              </div>
            )}
            {content.description && <p className="text-sm text-muted-foreground text-center mt-4">{content.description}</p>}
          </div>
        );
      
      case 'pdf_download':
        const alignmentClasses = {
          left: 'text-left',
          center: 'text-center',
          right: 'text-right'
        };
        const buttonVariants = {
          primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
          outline: 'border border-primary text-primary hover:bg-primary/10',
          ghost: 'text-primary hover:bg-primary/10'
        };
        
        return (
          <div className={`py-8 px-4 ${alignmentClasses[content.alignment || 'center']}`}>
            {content.title && <h2 className="text-xl font-semibold mb-2">{content.title}</h2>}
            {content.description && <p className="text-sm text-muted-foreground mb-4">{content.description}</p>}
            <button className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${buttonVariants[content.button_style || 'primary']}`}>
              <Download className="w-4 h-4" />
              {content.button_text || 'Download PDF'}
            </button>
            {!content.pdf_url && (
              <p className="text-xs text-muted-foreground mt-2">No PDF file selected</p>
            )}
          </div>
        );
      
      case 'document_list':
        const docs = content.documents || [];
        
        return (
          <div className="py-8 px-4">
            {content.title && <h2 className="text-xl font-semibold mb-2 text-center">{content.title}</h2>}
            {content.description && <p className="text-sm text-muted-foreground mb-6 text-center">{content.description}</p>}
            
            {docs.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {docs.map((doc, idx) => (
                  <div key={doc.id || idx} className="border rounded-lg overflow-hidden bg-card">
                    {doc.image_url ? (
                      <img src={doc.image_url} alt={doc.title} className="w-full h-24 object-cover" />
                    ) : (
                      <div className="w-full h-24 bg-muted flex items-center justify-center">
                        <FileText className="w-8 h-8 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="p-3">
                      <h3 className="font-medium text-sm truncate">{doc.title || 'Untitled'}</h3>
                      {doc.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>
                      )}
                      <div className="mt-2 flex items-center text-xs text-primary">
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No documents added</p>
              </div>
            )}
          </div>
        );
      
      case 'dynamic_agent':
        return (
          <div className="py-8 px-4">
            {content.title && <h2 className="text-xl font-semibold mb-2 text-center">{content.title}</h2>}
            
            {content.agent_id ? (
              <div className="text-center py-8 border-2 border-dashed rounded-lg bg-blue-50 dark:bg-blue-950">
                <Users className="w-10 h-10 mx-auto text-blue-500 mb-2" />
                <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Dynamic Agent Selected</p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Agent profile will be loaded from database</p>
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed rounded-lg">
                <Users className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">No agent selected</p>
                <p className="text-xs text-muted-foreground mt-1">Configure this section to select an agent</p>
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <div className="py-8 px-4 text-center text-muted-foreground">
            <p className="text-sm">[{type} section preview]</p>
          </div>
        );
    }
  };

  return (
    <div className="bg-background border rounded-lg overflow-hidden shadow-sm">
      {/* Preview Header */}
      <div className="bg-muted/50 px-3 py-2 border-b flex items-center gap-2">
        <div className="flex gap-1">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-muted-foreground ml-2">{getPagesDomain()}/p/{page?.slug || 'preview'}</span>
      </div>
      
      {/* Preview Content */}
      <div className="max-h-[600px] overflow-y-auto">
        {page?.sections?.filter(s => s.visible !== false).sort((a, b) => a.order - b.order).map((section) => (
          <div key={section.id}>
            {renderSection(section)}
          </div>
        ))}
        
        {(!page?.sections || page.sections.length === 0) && (
          <div className="py-16 text-center text-muted-foreground">
            <Layout className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Add sections to preview your page</p>
          </div>
        )}
        
        {/* Footer — the same component the live page renders, so this pane
            shows the real thing rather than a two-line stand-in. */}
        <BrandFooter brand={brand} />
      </div>
    </div>
  );
}

export default function PageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getPagesDomain, buildLandingPageUrl, urls } = useAppSettings();
  const [page, setPage] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');
  const [selectedSection, setSelectedSection] = useState(null);
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  
  // Save as Template state
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState('custom');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    if (id) {
      fetchPage();
      fetchBrand();
    }
  }, [id]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const data = await landingPagesApi.get(id);
      if (data.success) {
        setPage(data.data);
        if (data.data.sections?.length > 0) {
          setSelectedSection(data.data.sections[0].id);
        }
      } else {
        toast.error('Page not found');
        navigate('/pages');
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrand = async () => {
    try {
      const data = await brandApi.getSettings();
      if (data.success) {
        setBrand(data.data);
      }
    } catch (error) {
      console.error('Error fetching brand:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await landingPagesApi.update(id, {
        title: page.title,
        description: page.description,
        sections: page.sections,
        form: page.form,
        meta_title: page.meta_title,
        meta_description: page.meta_description,
        tracking_tags: page.tracking_tags || [],
        lead_tracking: page.lead_tracking || {},
        default_locale: page.default_locale || 'en',
        locales: page.locales || [],
        translations: page.translations || {},
      });
      if (data.success) {
        toast.success('Page saved!');
        setPage(data.data);
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      const data = await landingPagesApi.publish(id);
      if (data.success) {
        toast.success('Page published!');
        setPage(data.data);
      }
    } catch (error) {
      toast.error('Failed to publish');
    }
  };

  const handleUnpublish = async () => {
    try {
      const data = await landingPagesApi.unpublish(id);
      if (data.success) {
        toast.success('Page unpublished');
        setPage(data.data);
      }
    } catch (error) {
      toast.error('Failed to unpublish');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setSavingTemplate(true);
    try {
      const data = await landingPagesApi.saveAsTemplate(id, {
        name: templateName.trim(),
        description: templateDescription.trim(),
        category: templateCategory
      });
      
      if (data.success) {
        toast.success('Page saved as template!');
        setSaveTemplateOpen(false);
        setTemplateName('');
        setTemplateDescription('');
        setTemplateCategory('custom');
      } else {
        toast.error(data.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save as template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const addSection = (type) => {
    const newSection = {
      id: `section_${Date.now()}`,
      type,
      order: (page.sections?.length || 0) + 1,
      visible: true,
      content: {},
      style: {}
    };
    setPage({ ...page, sections: [...(page.sections || []), newSection] });
    setSelectedSection(newSection.id);
    setAddSectionOpen(false);
  };

  const updateSection = (sectionId, updates) => {
    setPage({
      ...page,
      sections: page.sections.map(s => s.id === sectionId ? { ...s, ...updates } : s)
    });
  };

  const deleteSection = (sectionId) => {
    setPage({
      ...page,
      sections: page.sections.filter(s => s.id !== sectionId)
    });
    setSelectedSection(null);
  };

  const moveSection = (sectionId, direction) => {
    const sections = [...page.sections].sort((a, b) => a.order - b.order);
    const idx = sections.findIndex(s => s.id === sectionId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === sections.length - 1)) return;
    
    const newIdx = idx + direction;
    const [removed] = sections.splice(idx, 1);
    sections.splice(newIdx, 0, removed);
    
    const reordered = sections.map((s, i) => ({ ...s, order: i + 1 }));
    setPage({ ...page, sections: reordered });
  };

  const currentSection = page?.sections?.find(s => s.id === selectedSection);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="page-builder-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-5">
            <Skeleton className="h-[600px]" />
          </div>
          <div className="col-span-7">
            <Skeleton className="h-[600px]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="page-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/pages')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{page?.title}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>/p/{page?.slug}</span>
              <Badge variant={page?.status === 'published' ? 'default' : 'secondary'}>
                {page?.status}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {page?.status === 'published' ? (
            <>
              <Button variant="outline" asChild>
                <a href={`https://${buildLandingPageUrl(page.slug)}`} target="_blank" rel="noopener noreferrer">
                  <Eye className="w-4 h-4 mr-2" />
                  View Live
                </a>
              </Button>
              <Button variant="outline" onClick={handleUnpublish}>
                <EyeOff className="w-4 h-4 mr-2" />
                Unpublish
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={handlePublish}>
              <Globe className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
          
          {/* Save as Template Button */}
          <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="save-as-template-btn">
                <FileStack className="w-4 h-4 mr-2" />
                Save as Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save Page as Template</DialogTitle>
                <DialogDescription>
                  Create a reusable template from this page's structure. The template will include all sections and form configuration.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="template-name">Template Name *</Label>
                  <Input
                    id="template-name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g., Property Listing Premium"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-description">Description</Label>
                  <Textarea
                    id="template-description"
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder="Describe what this template is best used for..."
                    rows={3}
                    data-testid="template-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-category">Category</Label>
                  <Select value={templateCategory} onValueChange={setTemplateCategory}>
                    <SelectTrigger data-testid="template-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                      <SelectItem value="real_estate">Real Estate</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="events">Events</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSaveTemplateOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveAsTemplate} 
                  disabled={savingTemplate || !templateName.trim()}
                  data-testid="confirm-save-template-btn"
                >
                  {savingTemplate ? 'Saving...' : 'Save Template'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Button onClick={handleSave} disabled={saving} data-testid="save-page-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Main Editor */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Controls */}
        <div className="col-span-5 space-y-4">
          <Card>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <CardHeader className="pb-0">
                <TabsList className="grid grid-cols-5">
                  <TabsTrigger value="sections">Sections</TabsTrigger>
                  <TabsTrigger value="form">Form</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="tracking">Tracking</TabsTrigger>
                  <TabsTrigger value="languages">Languages</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="pt-4">
                <TabsContent value="sections" className="mt-0">
                  <div className="space-y-4">
                    {/* Section List */}
                    <div className="space-y-2">
                      {page?.sections?.sort((a, b) => a.order - b.order).map((section) => (
                        <div
                          key={section.id}
                          onClick={() => setSelectedSection(section.id)}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                            selectedSection === section.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-muted-foreground/50'
                          }`}
                        >
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                          <span className="flex-1 text-sm font-medium capitalize">
                            {section.type.replace('_', ' ')}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); moveSection(section.id, -1); }}
                            >
                              <ChevronUp className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); moveSection(section.id, 1); }}
                            >
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={(e) => { e.stopPropagation(); deleteSection(section.id); }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Section */}
                    <Dialog open={addSectionOpen} onOpenChange={setAddSectionOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Section
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Section</DialogTitle>
                          <DialogDescription>Choose a section type to add to your page</DialogDescription>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-3 py-4">
                          {SECTION_TYPES.map((st) => (
                            <div
                              key={st.type}
                              onClick={() => addSection(st.type)}
                              className="p-3 border rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                            >
                              <st.icon className="w-5 h-5 mb-2 text-primary" />
                              <p className="font-medium text-sm">{st.label}</p>
                              <p className="text-xs text-muted-foreground">{st.description}</p>
                            </div>
                          ))}
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* Section Editor */}
                    {currentSection && (
                      <>
                        <Separator />
                        <div>
                          <h3 className="font-medium mb-3 capitalize">
                            Edit: {currentSection.type.replace('_', ' ')}
                          </h3>
                          <SectionEditor
                            section={currentSection}
                            onChange={(updated) => updateSection(currentSection.id, updated)}
                            pageId={page?.id}
                          />
                          <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                            <Switch
                              checked={currentSection.visible !== false}
                              onCheckedChange={(v) => updateSection(currentSection.id, { visible: v })}
                            />
                            <Label>Visible</Label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="form" className="mt-0">
                  <FormBuilder
                    form={page?.form}
                    onChange={(form) => setPage({ ...page, form })}
                  />
                </TabsContent>

                <TabsContent value="settings" className="mt-0">
                  <div className="space-y-4">
                    <div>
                      <Label>Page Title</Label>
                      <Input
                        value={page?.title || ''}
                        onChange={(e) => setPage({ ...page, title: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={page?.description || ''}
                        onChange={(e) => setPage({ ...page, description: e.target.value })}
                        rows={2}
                      />
                    </div>
                    <Separator />
                    <div>
                      <Label>SEO Title</Label>
                      <Input
                        value={page?.meta_title || ''}
                        onChange={(e) => setPage({ ...page, meta_title: e.target.value })}
                        placeholder={page?.title}
                      />
                    </div>
                    <div>
                      <Label>SEO Description</Label>
                      <Textarea
                        value={page?.meta_description || ''}
                        onChange={(e) => setPage({ ...page, meta_description: e.target.value })}
                        rows={2}
                      />
                    </div>
                    <Separator />
                    <div>
                      <Label>Custom tracking</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Analytics &amp; ad-platform tags for this page (GA4, Meta Pixel, and more).
                      </p>
                      <TrackingTagsEditor
                        value={page?.tracking_tags || []}
                        onChange={(tracking_tags) => setPage({ ...page, tracking_tags })}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tracking" className="mt-0">
                  <LeadTrackingEditor
                    page={page}
                    brand={brand}
                    value={page?.lead_tracking || {}}
                    onChange={(lead_tracking) => setPage({ ...page, lead_tracking })}
                  />
                </TabsContent>

                <TabsContent value="languages" className="mt-0">
                  <TranslationEditor page={page} onChange={setPage} />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>

        {/* Right: Preview */}
        <div className="col-span-7">
          <div className="sticky top-20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sm text-muted-foreground">Live Preview</h3>
              <Button variant="ghost" size="sm" asChild>
                <a href={`${(urls.pages_url || '').replace(/\/$/, '')}/p/${page?.slug}/preview`} target="_blank" rel="noopener noreferrer">
                  <Eye className="w-4 h-4 mr-1" />
                  Full Preview
                </a>
              </Button>
            </div>
            <LivePreview page={page} brand={brand} />
          </div>
        </div>
      </div>
    </div>
  );
}
